import React, { useState, useEffect } from 'react';
import { 
  AlertCircle, 
  Trash2, 
  RefreshCw, 
  DollarSign, 
  Clock, 
  Server,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { apiClient } from '../../services/api';

interface OrphanedDroplet {
  id: string;
  name: string;
  ip_address: string;
  age_hours: number;
  created_at: string;
  region?: string;
  size?: string;
  status?: string;
}

interface CleanupResult {
  success: boolean;
  orphans_found: number;
  orphans_destroyed: number;
  dry_run: boolean;
  details?: any[];
  errors?: string[];
}

export const OrphanedDroplets: React.FC = () => {
  const [orphans, setOrphans] = useState<OrphanedDroplet[]>([]);
  const [loading, setLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [totalCost, setTotalCost] = useState(0);
  const [selectedOrphans, setSelectedOrphans] = useState<Set<string>>(new Set());
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupOptions, setCleanupOptions] = useState({
    maxAge: 2,
    maxCount: 10,
    dryRun: true
  });

  const fetchOrphans = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await apiClient.get('/admin/droplets/orphaned');
      // apiClient.get returns the data directly, not the axios response
      setOrphans(response.orphaned_droplets || []);
      setTotalCost(response.estimated_wasted_cost || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch orphaned droplets');
    } finally {
      setLoading(false);
    }
  };

  const performCleanup = async () => {
    setCleanupLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const params = new URLSearchParams({
        dry_run: cleanupOptions.dryRun.toString(),
        max_age_hours: cleanupOptions.maxAge.toString(),
        max_count: cleanupOptions.maxCount.toString()
      });
      
      const response = await apiClient.post(`/admin/droplets/cleanup?${params}`);
      const result: CleanupResult = response;
      
      if (result.success) {
        if (result.dry_run) {
          setSuccessMessage(
            `Dry run complete: Would destroy ${result.orphans_destroyed} of ${result.orphans_found} orphaned droplets`
          );
        } else {
          setSuccessMessage(
            `Successfully destroyed ${result.orphans_destroyed} orphaned droplets`
          );
          // Refresh the list after actual cleanup
          await fetchOrphans();
        }
      }
    } catch (err: any) {
      setError(err.message || 'Cleanup failed');
    } finally {
      setCleanupLoading(false);
      setShowCleanupModal(false);
    }
  };

  const forceDestroyDroplet = async (dropletId: string) => {
    if (!window.confirm(`Are you sure you want to force destroy droplet ${dropletId}?`)) {
      return;
    }
    
    setError(null);
    try {
      const response = await apiClient.delete(`/admin/droplets/${dropletId}/force`);
      if (response.success) {
        setSuccessMessage(`Successfully destroyed droplet ${dropletId}`);
        await fetchOrphans();
      } else {
        setError(`Failed to destroy droplet: ${response.message}`);
      }
    } catch (err: any) {
      setError(err.message || 'Force destroy failed');
    }
  };

  const reconcileDatabase = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await apiClient.post('/admin/droplets/reconcile');
      const result = response.reconciliation;
      setSuccessMessage(
        `Reconciliation complete: ${result.runs_marked_failed} runs marked as failed, ${result.ips_updated} IPs updated`
      );
      await fetchOrphans();
    } catch (err: any) {
      setError(err.message || 'Reconciliation failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrphans();
  }, []);

  const formatAge = (hours: number): string => {
    if (hours < 1) return `${Math.round(hours * 60)} minutes`;
    if (hours < 24) return `${hours.toFixed(1)} hours`;
    return `${(hours / 24).toFixed(1)} days`;
  };

  const getSeverityColor = (ageHours: number): string => {
    if (ageHours > 48) return 'text-red-600';
    if (ageHours > 24) return 'text-orange-500';
    if (ageHours > 12) return 'text-yellow-600';
    return 'text-gray-600';
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg">
      <div className="px-6 py-4 border-b border-gray-700">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-white">Orphaned Droplets</h2>
            <p className="text-sm text-gray-400 mt-1">
              Manage DigitalOcean droplets without corresponding database records
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={reconcileDatabase}
              disabled={loading}
              className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              title="Reconcile database with DigitalOcean state"
            >
              <RefreshCw className="h-4 w-4 inline mr-1" />
              Reconcile
            </button>
            <button
              onClick={() => setShowCleanupModal(true)}
              disabled={loading || orphans.length === 0}
              className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4 inline mr-1" />
              Cleanup
            </button>
            <button
              onClick={fetchOrphans}
              disabled={loading}
              className="px-3 py-2 text-sm bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="px-6 py-3 bg-gray-900 border-b border-gray-700">
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center">
            <Server className="h-5 w-5 text-gray-400 mr-2" />
            <div>
              <p className="text-sm text-gray-400">Total Orphans</p>
              <p className="text-xl font-semibold text-white">{orphans.length}</p>
            </div>
          </div>
          <div className="flex items-center">
            <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
            <div>
              <p className="text-sm text-gray-400">Estimated Cost</p>
              <p className="text-xl font-semibold text-red-600">${totalCost.toFixed(2)}</p>
            </div>
          </div>
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-gray-400 mr-2" />
            <div>
              <p className="text-sm text-gray-400">Oldest Orphan</p>
              <p className="text-xl font-semibold text-orange-600">
                {orphans.length > 0 
                  ? formatAge(Math.max(...orphans.map(o => o.age_hours)))
                  : 'None'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
          <XCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {orphans.length > 5 && (
        <div className="mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-800">
              High number of orphaned droplets detected. Consider running cleanup to reduce costs.
            </p>
          </div>
        </div>
      )}

      {/* Orphans Table */}
      <div className="px-6 py-4">
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
            <p className="text-gray-400 mt-2">Loading orphaned droplets...</p>
          </div>
        ) : orphans.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
            <p className="text-white font-medium mt-2">No orphaned droplets found</p>
            <p className="text-gray-400 text-sm mt-1">All droplets are properly tracked</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Droplet
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Age
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Est. Cost
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Region
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {orphans.map((orphan) => (
                  <tr key={orphan.id} className="hover:bg-gray-700">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-white">{orphan.name}</p>
                        <p className="text-xs text-gray-400">ID: {orphan.id}</p>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <code className="text-sm text-gray-300">{orphan.ip_address}</code>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`text-sm font-medium ${getSeverityColor(orphan.age_hours)}`}>
                        {formatAge(orphan.age_hours)}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="text-sm text-gray-300">
                        ${(orphan.age_hours * 0.01).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-400">
                      {orphan.region || 'Unknown'}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => forceDestroyDroplet(orphan.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Force destroy this droplet"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cleanup Modal */}
      {showCleanupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-4">Cleanup Options</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Minimum Age (hours)
                </label>
                <input
                  type="number"
                  min="0"
                  value={cleanupOptions.maxAge}
                  onChange={(e) => setCleanupOptions({
                    ...cleanupOptions,
                    maxAge: parseInt(e.target.value) || 0
                  })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Only cleanup droplets older than this
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Maximum Count
                </label>
                <input
                  type="number"
                  min="1"
                  value={cleanupOptions.maxCount}
                  onChange={(e) => setCleanupOptions({
                    ...cleanupOptions,
                    maxCount: parseInt(e.target.value) || 1
                  })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Maximum droplets to destroy in one run
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="dryRun"
                  checked={cleanupOptions.dryRun}
                  onChange={(e) => setCleanupOptions({
                    ...cleanupOptions,
                    dryRun: e.target.checked
                  })}
                  className="h-4 w-4 text-blue-600 rounded border-gray-600"
                />
                <label htmlFor="dryRun" className="ml-2 text-sm text-gray-300">
                  Dry run (preview only, don't actually destroy)
                </label>
              </div>

              {!cleanupOptions.dryRun && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">
                    <strong>Warning:</strong> This will permanently destroy droplets. This action cannot be undone.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCleanupModal(false)}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={performCleanup}
                disabled={cleanupLoading}
                className={`px-4 py-2 text-sm text-white rounded-md ${
                  cleanupOptions.dryRun 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50`}
              >
                {cleanupLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin inline" />
                ) : cleanupOptions.dryRun ? (
                  'Preview Cleanup'
                ) : (
                  'Execute Cleanup'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};